import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Avatar,
  IconButton,
  useBreakpointValue,
  Flex,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

// Default configuration object
const defaultConfig = {
  title: "Check what these clients have to say",
  backgroundColor: "#F8F9FA",
  titleColor: "gray.800",
  textColor: "gray.700",
  authorColor: "gray.800",
  colorScheme: "blue",
  autoPlay: false,
  autoPlayInterval: 5000, // Slightly longer for better UX
  showAvatars: true,
  showNavigation: true,
  showDots: false,
  containerMaxWidth: "6xl",
  contentMaxWidth: "4xl"
};

const FeedbackSection = ({
  testimonials = [],
  config = {},
  theme = "default",
  ...overrideProps
}) => {
  // Merge configurations
  const finalConfig = {
    ...defaultConfig,
    ...config,
    ...overrideProps
  };

  const {
    title,
    backgroundColor,
    titleColor,
    textColor,
    authorColor,
    colorScheme,
    autoPlay,
    autoPlayInterval,
    showAvatars,
    showNavigation,
    showDots,
    containerMaxWidth,
    contentMaxWidth
  } = finalConfig;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState(1); // 1 for next, -1 for prev

  // Responsive configurations
  const headingFontSize = useBreakpointValue({
    base: "2xl",
    md: "3xl",
    lg: "4xl",
  });

  const testimonialFontSize = useBreakpointValue({
    base: "md",
    md: "lg",
    lg: "xl",
  });

  const authorFontSize = useBreakpointValue({
    base: "lg",
    md: "xl",
    lg: "2xl",
  });

  const avatarSize = useBreakpointValue({
    base: "md",
    md: "lg",
    lg: "xl",
  });

  // Fixed height for consistent section sizing
  const sectionMinHeight = useBreakpointValue({
    base: "500px",
    md: "550px",
    lg: "600px",
    xl: "650px",
  });

  const contentAreaHeight = useBreakpointValue({
    base: "280px",
    md: "320px",
    lg: "340px",
    xl: "400px",
  });

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && testimonials.length > 1) {
      const interval = setInterval(() => {
        setSlideDirection(1);
        setCurrentIndex((prev) => 
          prev === testimonials.length - 1 ? 0 : prev + 1
        );
      }, autoPlayInterval);

      return () => clearInterval(interval);
    }
  }, [autoPlay, autoPlayInterval, testimonials.length]);

  // Animation trigger
  useEffect(() => {
    setIsAnimating(true);
    const timeout = setTimeout(() => setIsAnimating(false), 700); // Match transition duration
    return () => clearTimeout(timeout);
  }, [currentIndex]);

  // Handle empty testimonials
  if (!testimonials || testimonials.length === 0) {
    return (
      <Box 
        py={{ base: 12, md: 16, lg: 20 }}
        minH={sectionMinHeight}
      >
        <Container maxW={containerMaxWidth}>
          <Flex align="center" justify="center" minH={contentAreaHeight}>
            <Text textAlign="center" color="gray.500">
              No testimonials available.
            </Text>
          </Flex>
        </Container>
      </Box>
    );
  }

  const handlePrevious = () => {
    if (isAnimating) return; // Prevent rapid clicks
    setSlideDirection(-1);
    setCurrentIndex((prev) => 
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    if (isAnimating) return; // Prevent rapid clicks
    setSlideDirection(1);
    setCurrentIndex((prev) => 
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };


  return (
    <Box 
      py={{ base: 8, md: 12, lg: 16 }}
      position="relative"
      minH={sectionMinHeight}
    >
      <Container maxW={containerMaxWidth}>
        <VStack align="center">
          
          {/* Quote Icon with subtle animation */}
          <Box
            fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
            color="#1E40AF"
            fontWeight="bold"
            lineHeight="1"
            opacity={isAnimating ? 0.7 : 1}
            transform={isAnimating ? "scale(0.9)" : "scale(1)"}
            transition="all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
          >
            ❝❞
          </Box>

          {/* Title */}
          <Heading
            fontSize={headingFontSize}
            fontWeight="bold"
            color={titleColor}
            textAlign="center"
            lineHeight="shorter"
            maxW="4xl"
            opacity={isAnimating ? 0.8 : 1}
            transition="opacity 0.3s ease-out"
          >
            {title}
          </Heading>

          {/* Main Content Container with Smooth Sliding */}
          <Box 
            w="full" 
            maxW="6xl" 
            position="relative"
            overflow="hidden"
            minH={{ base: "400px", md: "450px", lg: "500px" }}
            // bg="white"
            borderRadius="xl"
            // boxShadow="sm"
            p={{ base: 4, md: 6, lg: 8 }}
          >
            {/* Sliding Wrapper */}
            <Box
              display="flex"
              width={`${testimonials.length * 100}%`}
              transform={`translateX(-${(currentIndex * 100) / testimonials.length}%)`}
              transition="transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
              willChange="transform"
            >
              {testimonials.map((testimonial, index) => (
                <Flex 
                  key={testimonial.id || index}
                  align="center" 
                  justify="center" 
                  position="relative"
                  minH={contentAreaHeight}
                  width={`${100 / testimonials.length}%`}
                  flexShrink={0}
                >
                  {/* Testimonial Content with Staggered Animations */}
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    textAlign="center"
                    px={{ base: 8, md: 12, lg: 16 }}
                    minH={contentAreaHeight}
                    w="full"
                  >
                    {/* Testimonial Text Container with Smooth Transition */}
                    <Box
                      flex="1"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      minH={{ base: "200px", md: "220px", lg: "240px" }}
                      maxH={{ base: "300px", md: "320px", lg: "350px" }}
                      overflow="hidden"
                      w="full"
                      opacity={currentIndex === index ? 1 : 0.6}
                      transform={currentIndex === index ? "translateY(0)" : "translateY(20px)"}
                      transition="all 0.6s ease-out 0.1s"
                    >
                      <Text
                        fontSize={testimonialFontSize}
                        color={textColor}
                        textAlign="center"
                        lineHeight="tall"
                        maxW={contentMaxWidth}
                        fontWeight="normal"
                        px={{ base: 4, md: 6 }}
                        noOfLines={{ base: 8, md: 10, lg: 12 }}
                        sx={{
                          // Better text overflow handling
                          display: "-webkit-box",
                          WebkitLineClamp: { base: 8, md: 10, lg: 12 },
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          wordBreak: "break-word",
                          whiteSpace: "pre-line" // Handle line breaks properly
                        }}
                      >
                        "{testimonial.text && testimonial.text.length > 500 
                          ? `${testimonial.text.substring(0, 500)}...` 
                          : testimonial.text
                        }"
                      </Text>
                    </Box>

                    {/* Author Section with Staggered Animation */}
                    <Box
                      minH={{ base: "60px", md: "70px", lg: "80px" }}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      w="full"
                      opacity={currentIndex === index ? 1 : 0.5}
                      transform={currentIndex === index ? "translateY(0)" : "translateY(25px)"}
                      transition="all 0.6s ease-out 0.2s"
                    >
                      <Text
                        fontSize={authorFontSize}
                        fontWeight="semibold"
                        color={authorColor}
                        textAlign="center"
                      >
                        - {testimonial.author}
                      </Text>
                    </Box>

                    {/* Avatar Group with Final Animation */}
                    {showAvatars && testimonial.avatars && testimonial.avatars.length > 0 && (
                      <Box
                        minH={{ base: "60px", md: "70px", lg: "80px" }}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w="full"
                        opacity={currentIndex === index ? 1 : 0.4}
                        transform={currentIndex === index ? "translateY(0) scale(1)" : "translateY(30px) scale(0.9)"}
                        transition="all 0.7s ease-out 0.3s"
                      >
                        <HStack spacing={-3} justify="center">
                          {testimonial.avatars.map((avatar, avatarIndex) => (
                            <Avatar
                              key={`avatar-${index}-${avatarIndex}`}
                              size={avatarSize}
                              src={avatar}
                              name={`User ${avatarIndex + 1}`}
                              border="4px solid white"
                              boxShadow="lg"
                              transition="transform 0.3s ease-out"
                              _hover={{
                                transform: "scale(1.1)",
                                zIndex: 10,
                              }}
                              style={{
                                transitionDelay: `${avatarIndex * 0.1}s` // Stagger avatar animations
                              }}
                            />
                          ))}
                        </HStack>
                      </Box>
                    )}
                  </Flex>
                </Flex>
              ))}
            </Box>

            {/* Enhanced Left Arrow */}
            {showNavigation && testimonials.length > 1 && (
              <IconButton
                aria-label="Previous testimonial"
                icon={<ChevronLeftIcon boxSize={6} />}
                variant="solid"
                colorScheme={colorScheme}
                size="md"
                borderRadius="full"
                position="absolute"
                left={{ base: 2, md: 4, lg: 6 }}
                top="50%"
                transform="translateY(-50%)"
                zIndex={2}
                disabled={isAnimating}
                bg="white"
                color={`${colorScheme}.500`}
                boxShadow="lg"
                border="2px solid"
                borderColor={`${colorScheme}.100`}
                _hover={{
                  bg: `${colorScheme}.50`,
                  transform: "translateY(-50%) scale(1.1)",
                  shadow: "xl",
                  borderColor: `${colorScheme}.200`,
                }}
                _active={{
                  transform: "translateY(-50%) scale(0.95)",
                }}
                _disabled={{
                  opacity: 0.5,
                  cursor: "not-allowed",
                }}
                transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                onClick={handlePrevious}
              />
            )}

            {/* Enhanced Right Arrow */}
            {showNavigation && testimonials.length > 1 && (
              <IconButton
                aria-label="Next testimonial"
                icon={<ChevronRightIcon boxSize={6} />}
                variant="solid"
                colorScheme={colorScheme}
                size="md"
                borderRadius="full"
                position="absolute"
                right={{ base: 2, md: 4, lg: 6 }}
                top="50%"
                transform="translateY(-50%)"
                zIndex={2}
                disabled={isAnimating}
                bg="white"
                color={`${colorScheme}.500`}
                boxShadow="lg"
                border="2px solid"
                borderColor={`${colorScheme}.100`}
                _hover={{
                  bg: `${colorScheme}.50`,
                  transform: "translateY(-50%) scale(1.1)",
                  shadow: "xl",
                  borderColor: `${colorScheme}.200`,
                }}
                _active={{
                  transform: "translateY(-50%) scale(0.95)",
                }}
                _disabled={{
                  opacity: 0.5,
                  cursor: "not-allowed",
                }}
                transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                onClick={handleNext}
              />
            )}

            {/* Dots Navigation Indicator */}
            {showDots && testimonials.length > 1 && (
              <HStack spacing={3} justify="center" mt={6}>
                {testimonials.map((_, index) => (
                  <Box
                    key={index}
                    w={{ base: 2, md: 3 }}
                    h={{ base: 2, md: 3 }}
                    borderRadius="full"
                    bg={currentIndex === index ? `${colorScheme}.500` : `${colorScheme}.200`}
                    cursor="pointer"
                    transition="all 0.3s ease"
                    _hover={{
                      bg: `${colorScheme}.400`,
                      transform: "scale(1.2)",
                    }}
                    onClick={() => {
                      if (!isAnimating && index !== currentIndex) {
                        setSlideDirection(index > currentIndex ? 1 : -1);
                        setCurrentIndex(index);
                      }
                    }}
                  />
                ))}
              </HStack>
            )}
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default FeedbackSection;
